import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type {
  InsightQueryDescriptor,
  InsightQueryRunResponse,
  RunQueryArgs,
} from './types';

/**
 * Mirrors /portal/src/app/services/insightServices.js (the dashboard
 * subset). Endpoints:
 * GET /insight/v1/query → list of available query descriptors
 * GET /insight/v1/query/{queryId}/run()?... → run() one query and return its rows
 * RTK Query auto-keys the cache off the run() args, so the * `${queryId}-${dateRange}-${viewAsId}` cache key is implicit here — pass
 * the same combination and you get the same cache slot.
 * 5-minute freshness check.
 */
export const insightApi = createApi({
  reducerPath: 'insightApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['InsightQuery', 'InsightQueryRun'],
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    /** GET /insight/v1/query — list of dashboard-eligible queries. */
    getAllQueries: builder.query<InsightQueryDescriptor[], void>({
      query: () => ({
        url: `${services.insight}/v1/query`,
        method: 'GET',
      }),
      transformResponse: (
        response: InsightQueryDescriptor[] | { queries?: InsightQueryDescriptor[]; data?: InsightQueryDescriptor[] },
      ): InsightQueryDescriptor[] => {
        if (Array.isArray(response)) return response;
        return response.queries ?? response.data ?? [];
      },
      providesTags: ['InsightQuery'],
    }),

    /**
 * GET /insight/v1/query/{queryId}/run()?startDate=...&runAs=...&merchantIds=...&vendorIds=...
 * `startDate` is computed from `dateRange` (days back from today), matching
 * the helper exactly — see `insightServices.js:34-37`.
 */
    runQueryById: builder.query<InsightQueryRunResponse, RunQueryArgs>({
      query: ({ queryId, dateRange, runAs, merchantIds, vendorIds }) => {
        const qs = new URLSearchParams();
        if (dateRange) {
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - dateRange);
          const isoDay = startDate.toISOString().split('T')[0];
          if (isoDay) qs.append('startDate', isoDay);
        }
        if (runAs) qs.append('runAs', runAs);
        if (merchantIds) for (const m of merchantIds) qs.append('merchantIds', m);
        if (vendorIds) for (const v of vendorIds) qs.append('vendorIds', v);
        return {
          url: `${services.insight}/v1/query/${encodeURIComponent(queryId)}/run?${qs.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (
        response: InsightQueryRunResponse | { data?: InsightQueryRunResponse['data']; refreshedAt?: string },
      ): InsightQueryRunResponse => {
        // Normalize: server returns either `{ data, refreshedAt }` or a bare
        // array (older envs). Coerce to the wrapper shape.
        if (Array.isArray(response)) return { data: response };
        return {
          data: response.data ?? [],
          refreshedAt: response.refreshedAt,
        };
      },
      providesTags: (_r, _e, { queryId, dateRange, viewAsId }) => [
        { type: 'InsightQueryRun', id: `${queryId}-${dateRange}-${viewAsId}` },
      ],
    }),
  }),
});

export const { useGetAllQueriesQuery, useRunQueryByIdQuery, useLazyRunQueryByIdQuery } = insightApi;
